/**
 * Las mismas celdas del mapa, en una tabla.
 *
 * No es un extra: un `<canvas>` de WebGL no se navega con el teclado ni lo lee
 * un lector de pantalla, así que sin esto el módulo entero sería inaccesible.
 * Y es además lo que un auditor pide primero — el mapa muestra dónde, la tabla
 * dice cuánto y se puede leer en un papel.
 *
 * Cada fila enfoca su celda en el mapa. Es un `<button>` de verdad, no un `tr`
 * con `onClick`: así entra en el orden de tabulación y responde a Enter y
 * espacio sin que haya que reimplementar nada.
 *
 * Lo que la tabla NO muestra: nada por escaneo. La fila más fina que existe en
 * este módulo es una celda geográfica con al menos `min_scans` escaneos.
 */
import type { ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Table, Td, Th } from '@/ui/Table';
import { shareOf, type HeatCell } from './cells';

export interface CellTableProps {
  /** Ya ordenadas por escaneos descendente. */
  cells: readonly HeatCell[];
  totalScans: number;
  selectedCell: string | null;
  onSelect: (cell: HeatCell) => void;
}

export function CellTable({
  cells,
  totalScans,
  selectedCell,
  onSelect,
}: CellTableProps): ReactNode {
  const { t, fmt } = useI18n();

  return (
    <Table caption={t('map.table.caption')}>
      <thead>
        <tr>
          <Th>{t('map.table.cell')}</Th>
          <Th>{t('map.table.country')}</Th>
          <Th className="text-right">{t('map.table.scans')}</Th>
          <Th className="text-right">{t('map.table.share')}</Th>
          <Th className="text-right">{t('map.table.suspicious')}</Th>
          <Th className="text-right">{t('map.table.peakUnits')}</Th>
        </tr>
      </thead>
      <tbody>
        {cells.map((cell) => (
          <tr
            key={cell.cell}
            className={cell.cell === selectedCell ? 'bg-accent-soft' : undefined}
            aria-selected={cell.cell === selectedCell}
          >
            <Td>
              <button
                type="button"
                onClick={() => {
                  onSelect(cell);
                }}
                className="rounded font-mono text-xs text-accent underline-offset-2 hover:underline"
                // El geohash solo no le dice nada a nadie: el nombre accesible
                // lleva las coordenadas del centroide, que es lo que se busca
                // en un informe.
                aria-label={t('map.table.focus', {
                  lat: fmt.number(cell.lat, 3),
                  lon: fmt.number(cell.lon, 3),
                })}
              >
                {cell.cell}
              </button>
              <span className="ml-2 text-xs text-ink-subtle">
                {fmt.number(cell.lat, 3)}, {fmt.number(cell.lon, 3)}
              </span>
            </Td>
            <Td className="text-ink-muted">{cell.country_code ?? '—'}</Td>
            <Td className="text-right tabular-nums">{fmt.number(cell.scans)}</Td>
            <Td className="text-right tabular-nums text-ink-muted">
              {fmt.percent(shareOf(cell, totalScans), 2)}
            </Td>
            <Td className="text-right">
              {cell.suspicious > 0 ? (
                <Badge tone="critical">{fmt.number(cell.suspicious)}</Badge>
              ) : (
                <span className="text-ink-subtle">—</span>
              )}
            </Td>
            <Td className="text-right tabular-nums text-ink-muted">
              {fmt.number(cell.peak_daily_units)}
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
